import React, { useState, useRef, useEffect } from 'react';
import EmailEditor from 'react-email-editor';
import juice from 'juice';
import '../styles/footer-designer.css';
import api from '../utils/api';
import { toast } from 'react-toastify';
import EmailCompatibilityChecker from './EmailCompatibilityChecker';
import EmailPreview from './EmailPreview';
import {
  XMarkIcon,
  EyeIcon,
  CodeBracketIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const FooterDesigner = ({ initialContent = '', initialDesign = null, onContentChange, onClose, footerData, onFooterDataChange, onSave }) => {
  const [content, setContent] = useState(initialContent);
  const [viewMode, setViewMode] = useState('split'); // split, editor, preview, code, compatibility
  const [previewVisible, setPreviewVisible] = useState(false); // Toggle for preview panel - closed by default
  const [sidebarVisible, setSidebarVisible] = useState(true); // Toggle for sidebar
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved changes
  const [designSettings, setDesignSettings] = useState({
    backgroundColor: '#f8f9fa',
    containerWidth: '600px',
    fontFamily: 'Arial, sans-serif',
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    textColor: '#666666',
    borderColor: '#dee2e6',
    useGradient: false,
    gradientStartColor: '#667eea',
    gradientEndColor: '#764ba2',
    gradientDirection: 'to right'
  });
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);

  const emailEditorRef = useRef(null);

  // UseEffect to hide Unlayer branding and Dev button
  useEffect(() => {
    const hideUnlayerBranding = () => {
      try {
        // Find the Unlayer iframe
        const iframe = document.querySelector('iframe[id^="editor-"]');
        if (iframe && iframe.contentDocument) {
          const iframeDoc = iframe.contentDocument;

          // Hide branding links
          const brandingLinks = iframeDoc.querySelectorAll('a[href*="unlayer.com"]');
          brandingLinks.forEach(el => {
            if (el.parentElement) {
              el.parentElement.style.display = 'none';
              el.parentElement.style.visibility = 'hidden';
              el.parentElement.style.height = '0';
              el.parentElement.style.overflow = 'hidden';
            }
            el.style.display = 'none';
          });

          // Hide footer elements  
          const footers = iframeDoc.querySelectorAll('footer');
          footers.forEach(el => {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.height = '0';
          });

          // Hide Dev button
          const devButtons = iframeDoc.querySelectorAll('button[title="Dev"]');
          devButtons.forEach(el => {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
          });
        }
      } catch (error) {
        // Ignore cross-origin errors silently
      }
    };

    // Run immediately and then every 500ms to catch dynamically added elements
    hideUnlayerBranding();
    const interval = setInterval(hideUnlayerBranding, 500);

    return () => clearInterval(interval);
  }, []);

  // Store the design to load after editor is ready
  const [designToLoad, setDesignToLoad] = React.useState(null);

  // Parse and prepare design when initialDesign changes
  React.useEffect(() => {
    if (initialDesign) {
      let parsedDesign = initialDesign;
      if (typeof initialDesign === 'string') {
        try {
          parsedDesign = JSON.parse(initialDesign);
          console.log('Parsed design from string:', parsedDesign);
        } catch (e) {
          console.error('Failed to parse initialDesign:', e);
          parsedDesign = null;
        }
      }
      
      if (parsedDesign && typeof parsedDesign === 'object' && Object.keys(parsedDesign).length > 0) {
        console.log('Design prepared for loading:', parsedDesign);
        setDesignToLoad(parsedDesign);
      }
    }
  }, [initialDesign]);

  // Initialize email editor with default design
  const onEmailEditorLoad = () => {
    console.log('Email editor loaded');
  };

  // Load design when editor is ready
  const onEmailEditorReady = () => {
    console.log('Email editor ready, designToLoad:', designToLoad);
    
    if (emailEditorRef.current) {
      if (designToLoad) {
        console.log('Loading saved design into editor:', designToLoad);
        emailEditorRef.current.editor.loadDesign(designToLoad);
        // Export HTML after loading design
        setTimeout(() => exportHtml(), 500);
      } else {
        console.log('No saved design found, loading default template');
          // Set default appearance
          emailEditorRef.current.editor.loadDesign({
          body: {
            rows: [
              {
                cells: [1],
                columns: [
                  {
                    contents: [
                      {
                        type: 'text',
                        values: {
                          containerPadding: '10px',
                          _meta: {
                            htmlID: 'u_content_text_1',
                            htmlClassNames: 'u_content_text'
                          },
                          selectable: true,
                          draggable: true,
                          duplicatable: true,
                          deletable: true,
                          hideable: true,
                          text: initialContent || '<p style="font-size: 14px; line-height: 140%; text-align: center;">Enter your footer content here...</p>'
                        }
                      }
                    ],
                    values: {
                      backgroundColor: designSettings.backgroundColor,
                      padding: '0px',
                      border: {},
                      _meta: { htmlID: 'u_column_1', htmlClassNames: 'u_column' }
                    }
                  }
                ],
                values: {
                  displayCondition: null,
                  columns: false,
                  backgroundColor: '',
                  columnsBackgroundColor: '',
                  backgroundImage: {
                    url: '',
                    fullWidth: true,
                    repeat: false,
                    center: true,
                    cover: false
                  },
                  padding: '0px',
                  hideDesktop: false,
                  _meta: { htmlID: 'u_row_1', htmlClassNames: 'u_row' }
                }
              }
            ],
            values: {
              backgroundColor: '#e7e7e7',
              backgroundImage: {
                url: '',
                fullWidth: true,
                repeat: false,
                center: true,
                cover: false
              },
              contentWidth: designSettings.containerWidth,
              contentAlign: 'center',
              fontFamily: {
                label: 'Arial',
                value: designSettings.fontFamily
              },
              preheaderText: '',
              linkStyle: {
                body: true,
                linkColor: designSettings.primaryColor,
                linkHoverColor: designSettings.secondaryColor,
                linkUnderline: true,
                linkHoverUnderline: true
              },
              _meta: { htmlID: 'u_body', htmlClassNames: 'u_body' }
            }
          }
        });
        // Export HTML after loading default design
        setTimeout(() => exportHtml(), 500);
        }
    }
  };

  // Helper function to decode HTML entities to prevent display issues
  const decodeHTMLEntities = (html) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  // Export HTML and Design from email editor
  const exportHtml = () => {
    if (emailEditorRef.current) {
      setLoading(true);
      emailEditorRef.current.editor.exportHtml(async (data) => {
        const { html } = data;

        // Also save the design JSON
        emailEditorRef.current.editor.saveDesign((design) => {
          try {
            // TEMP: Skip juice to test if it's causing the encoding issue
            let processedHtml = html; // Direct HTML without juice

            setHtmlContent(processedHtml);
            // Pass both HTML and Design JSON to parent
            onContentChange(processedHtml, design);
            setContent(processedHtml);
            setHasUnsavedChanges(true); // Mark as having unsaved changes
            setLoading(false);

          } catch (error) {
            console.error('Error processing HTML:', error);
            setHtmlContent(html);
            onContentChange(html, design);
            setContent(html);
            setHasUnsavedChanges(true); // Mark as having unsaved changes
            setLoading(false);
          }
        });
      });
    }
  };

  // Auto-export on blur or changes - REMOVED auto-interval to prevent input field interference
  // Users can manually refresh using the "Refresh Preview" button
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     exportHtml();
  //   }, 3000); // Auto-save every 3 seconds
  //
  //   return () => clearInterval(interval);
  // }, []);

  // Handle image upload through API
  const handleImageUpload = async (file, done) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `http://localhost:5009${response.data.file.path}`;
      done({ progress: 100, url: imageUrl });

    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
      done({ progress: 0 });
    }
  };

  const applyDesignSettings = () => {
    if (emailEditorRef.current) {
      // Determine background color/gradient
      let backgroundValue = '#e7e7e7';

      if (designSettings.useGradient) {
        if (designSettings.gradientDirection === 'radial') {
          backgroundValue = `radial-gradient(circle, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`;
        } else {
          backgroundValue = `linear-gradient(${designSettings.gradientDirection}, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`;
        }
      } else {
        backgroundValue = designSettings.backgroundColor;
      }

      emailEditorRef.current.editor.setBodyValues({
        contentWidth: designSettings.containerWidth,
        fontFamily: {
          label: designSettings.fontFamily.split(',')[0].trim(),
          value: designSettings.fontFamily
        },
        backgroundColor: backgroundValue
      });

      toast.success('Design settings applied');
      exportHtml();
    }
  };

  const getCodePreview = () => {
    return htmlContent || '<p>No content yet. Start designing your footer!</p>';
  };

  const handleSave = async () => {
    if (!footerData?.name) {
      toast.error('Please provide a footer name');
      return;
    }

    if (emailEditorRef.current) {
      setLoading(true);
      emailEditorRef.current.editor.exportHtml((data) => {
        const { html } = data;
        emailEditorRef.current.editor.saveDesign(async (design) => {
          try {
            if (onSave) {
              // Update local state for consistency
              onContentChange(html, design);
              setHasUnsavedChanges(false);

              // Pass the latest data to the save handler
              await onSave(html, design);
            }
          } catch (error) {
            console.error('Save error:', error);
          } finally {
            setLoading(false);
          }
        });
      });
    } else {
      // Fallback if editor not ready (shouldn't happen)
      if (onSave) await onSave();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-50 flex">
      {/* Sidebar Toggle Button (when collapsed) */}
      {!sidebarVisible && (
        <button
          onClick={() => setSidebarVisible(true)}
          className="absolute left-4 top-4 z-20 p-3 bg-white border border-gray-300 rounded-lg shadow-lg hover:bg-gray-50 transition-all"
          title="Show Sidebar"
        >
          <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Left Sidebar - Settings */}
      {sidebarVisible && (
        <div className="w-80 bg-white shadow-xl overflow-y-auto flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Footer Designer</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSidebarVisible(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Hide Sidebar"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Footer Settings */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Footer Settings
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Footer Name *</label>
                <input
                  type="text"
                  value={footerData?.name || ''}
                  onChange={(e) => onFooterDataChange({ ...footerData, name: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Professional Footer"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={footerData?.description || ''}
                  onChange={(e) => onFooterDataChange({ ...footerData, description: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholder="Brief description of this footer"
                  rows="2"
                />
              </div>
            </div>
          </div>


          {/* View Mode Selector */}
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">View Mode</h3>

            <div className="space-y-2">
              <button
                onClick={() => setViewMode('split')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'split' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Side-by-Side
              </button>
              <button
                onClick={() => setViewMode('editor')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'editor' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Editor Only
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'preview' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                Preview Only
              </button>
              <button
                onClick={() => setViewMode('compatibility')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'compatibility' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Compatibility Check
              </button>
              <button
                onClick={() => setViewMode('code')}
                className={`w-full px-3 py-2 text-sm rounded transition-colors flex items-center ${viewMode === 'code' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <CodeBracketIcon className="h-4 w-4 mr-2" />
                HTML Code
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b border-gray-200">
            <div className="mb-2 text-xs text-gray-600 font-medium">
              💡 Click to update preview with your latest changes
            </div>
            <button
              onClick={exportHtml}
              disabled={loading}
              className="w-full px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh Preview'}
            </button>
          </div>


          {/* Save/Cancel Buttons - Sticky at bottom */}
          <div className="mt-auto p-4 border-t border-gray-200 bg-gray-50">
            {hasUnsavedChanges && (
              <div className="mb-3 px-3 py-2 bg-orange-100 border border-orange-300 rounded-lg flex items-center text-xs text-orange-800">
                <svg className="h-4 w-4 mr-2 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Unsaved changes! Click Save to apply.</span>
              </div>
            )}
            <div className="space-y-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className={`w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center relative disabled:opacity-50 ${hasUnsavedChanges ? 'ring-2 ring-orange-400 ring-offset-2' : ''}`}
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {footerData?.id ? 'Update Footer' : 'Save Footer'}
                  </>
                )}
                {hasUnsavedChanges && !loading && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-orange-500 rounded-full animate-pulse"></span>
                )}
              </button>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col bg-gray-100">
        {/* Editor & Preview */}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'split' && (
            <div className="h-full flex relative">
              {/* Editor */}
              <div className={`border-r border-gray-300 transition-all duration-300 ${previewVisible ? 'flex-1' : 'w-full'}`}>
                <div className="h-full">
                  <EmailEditor
                    ref={emailEditorRef}
                    onLoad={onEmailEditorLoad}
                    onReady={onEmailEditorReady}
                    options={{
                      appearance: {
                        theme: 'light',
                        panels: {
                          tools: {
                            dock: 'right',
                            enabled: false
                          }
                        }
                      },
                      features: {
                        imageEditor: true,
                        stockImages: false,
                        preview: false,
                        smartMergeTags: false
                      },
                      tools: {
                        image: {
                          enabled: true,
                          properties: {
                            src: {
                              value: {
                                url: '',
                                width: 200,
                                height: 200,
                                autoWidth: false,
                                maxWidth: '100%'
                              }
                            }
                          }
                        }
                      },
                      customCSS: [
                        'body { font-family: ' + designSettings.fontFamily + '; }',
                        '#u-row-1 { background-color: transparent !important; }',
                        '.blockbuilder-tools-panel { display: none !important; }',
                        '.blockbuilder-panel { display: none !important; }',
                        '.unlayer-branding { display: none !important; visibility: hidden !important; }',
                        '[class*="branding"] { display: none !important; }',
                        '[class*="Branding"] { display: none !important; }',
                        'a[href*="unlayer.com"] { display: none !important; }',
                        'div[class*="powered"] { display: none !important; }',
                        'div[class*="Powered"] { display: none !important; }',
                        'button[title*="Dev"] { display: none !important; }',
                        'button[class*="dev-mode"] { display: none !important; }',
                        '[class*="DevMode"] { display: none !important; }',

                        // Gradient CSS Classes - Available for use in editor
                        // Linear Gradients
                        '.gradient-purple { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-blue { background: linear-gradient(135deg, #6B73FF 0%, #000DFF 100%) !important; }',
                        '.gradient-ocean { background: linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%) !important; }',
                        '.gradient-sunset { background: linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 90%, #2BFF88 100%) !important; }',
                        '.gradient-fire { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important; }',
                        '.gradient-mint { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important; }',
                        '.gradient-peach { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important; }',
                        '.gradient-sky { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%) !important; }',
                        '.gradient-rose { background: linear-gradient(135deg, #f6d365 0%, #fda085 100%) !important; }',
                        '.gradient-emerald { background: linear-gradient(135deg, #5ee7df 0%, #b490ca 100%) !important; }',
                        '.gradient-night { background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%) !important; }',
                        '.gradient-warm { background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%) !important; }',
                        '.gradient-cool { background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%) !important; }',
                        '.gradient-cosmic { background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%) !important; }',
                        '.gradient-forest { background: linear-gradient(135deg, #134e5e 0%, #71b280 100%) !important; }',

                        // Radial Gradients
                        '.gradient-radial-purple { background: radial-gradient(circle, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-radial-blue { background: radial-gradient(circle, #6B73FF 0%, #000DFF 100%) !important; }',
                        '.gradient-radial-sunset { background: radial-gradient(circle, #FA8BFF 0%, #2BD2FF 100%) !important; }',

                        // Directional Variations
                        '.gradient-horizontal { background: linear-gradient(to right, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-vertical { background: linear-gradient(to bottom, #667eea 0%, #764ba2 100%) !important; }',
                        '.gradient-diagonal-up { background: linear-gradient(to top right, #667eea 0%, #764ba2 100%) !important; }',

                        // Custom gradient from design settings
                        '.gradient-custom { background: ' + (designSettings.useGradient
                          ? (designSettings.gradientDirection === 'radial'
                            ? `radial-gradient(circle, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`
                            : `linear-gradient(${designSettings.gradientDirection}, ${designSettings.gradientStartColor}, ${designSettings.gradientEndColor})`)
                          : 'linear-gradient(135deg, #667eea, #764ba2)') + ' !important; }'
                      ],
                      mergeTags: {},
                      displayMode: 'email',
                      locale: 'en-US'
                    }}
                    tools={{
                      image: {
                        enabled: true,
                        properties: {
                          src: {
                            value: {
                              url: '',
                              width: 'auto',
                              height: 'auto',
                              autoWidth: true,
                              maxWidth: '100%'
                            }
                          }
                        }
                      }
                    }}
                    projectId={null}
                    minHeight="100%"
                  />
                </div>
              </div>

              {/* Toggle Preview Button */}
              <button
                onClick={() => setPreviewVisible(!previewVisible)}
                className={`absolute bottom-4 z-10 p-2 bg-white border border-gray-300 rounded-full shadow-lg hover:bg-gray-50 transition-all ${previewVisible ? 'right-4' : 'right-4'
                  }`}
                title={previewVisible ? 'Hide Preview' : 'Show Preview'}
              >
                {previewVisible ? (
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                )}
              </button>

              {/* Preview */}
              {previewVisible && (
                <div className="flex-1 bg-white overflow-auto">
                  <EmailPreview
                    htmlContent={htmlContent}
                    designSettings={designSettings}
                    mode="footer"
                  />
                </div>
              )}
            </div>
          )}

          {viewMode === 'editor' && (
            <div className="h-full">
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onEmailEditorLoad}
                onReady={onEmailEditorReady}
                options={{
                  appearance: {
                    theme: 'light',
                    panels: {
                      tools: {
                        dock: 'right',
                        enabled: false
                      }
                    }
                  },
                  features: {
                    imageEditor: true,
                    stockImages: false,
                    preview: false
                  },
                  displayMode: 'email',
                  locale: 'en-US'
                }}
                minHeight="100%"
              />
            </div>
          )}

          {viewMode === 'preview' && (
            <div className="h-full bg-white overflow-auto">
              <EmailPreview
                htmlContent={htmlContent}
                designSettings={designSettings}
                mode="footer"
              />
            </div>
          )}

          {viewMode === 'compatibility' && (
            <div className="h-full overflow-auto p-6 bg-gray-50">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Email Compatibility Check</h2>
                  <EmailCompatibilityChecker
                    htmlContent={htmlContent}
                    mode="footer"
                  />
                </div>
              </div>
            </div>
          )}

          {viewMode === 'code' && (
            <div className="h-full p-6 bg-gray-900 overflow-auto">
              <div className="max-w-6xl mx-auto">
                <div className="bg-gray-800 rounded-lg overflow-hidden shadow-xl">
                  <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gray-900">
                    <h4 className="text-white font-medium">HTML Code (with Inlined CSS)</h4>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(getCodePreview());
                        toast.success('Code copied to clipboard');
                      }}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      Copy Code
                    </button>
                  </div>
                  <textarea
                    value={getCodePreview()}
                    readOnly
                    className="w-full p-6 bg-gray-800 text-green-400 font-mono text-sm resize-none border-0 focus:outline-none"
                    style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}
                    spellCheck="false"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FooterDesigner;